import React, { useState } from "react";
import {
    Checkbox,
    DialogActions,
    FormControlLabel,
    Radio,
    RadioGroup,
} from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import DialogLayout from "../../../Components/DialogLayout";
import ButtonField from "../../../Components/Ui/ButtonField";
import { useTailwindBrand } from "../../../Components/shared";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import TravelRepository, { TravelInfo } from "../../../Repositories/TravelRepository";
import Teachr from "../../../Objects/models/Teachr";
import AddressChangeAlert from "./AddressChangeAlert";
import { SHOW_ERROR } from "../../../Redux/Reducers/MessageReducers";
import { useDispatch } from "react-redux";

const FILE_TYPES = ["PDF", "PNG", "JPEG", "JPG"];

interface AttestationModalProps {
    open: boolean;
    teachr: Teachr;
    clientId: number;
    clientName: string;
    travelInfo: TravelInfo;
    onSigned: () => void;
    onClose: () => void;
}

/**
 * Attestation sur l'honneur pré-remplie (scénario 60–160 km) : adresses,
 * distance, mode de transport à cocher, signature électronique en un clic.
 * Signée une seule fois par client tant que l'adresse ne change pas.
 * Justificatifs optionnels uploadables après signature.
 */
const AttestationModal = ({ open, teachr, clientId, clientName, travelInfo, onSigned, onClose }: AttestationModalProps) => {
    const { classes, common } = useTailwindBrand();
    const dispatch = useDispatch();
    const [mode, setMode] = useState("");
    const [accepted, setAccepted] = useState(false);
    const [documents, setDocuments] = useState<File[]>([]);
    const [load, setLoad] = useState(false);
    const [error, setError] = useState("");

    const teachrAddress = travelInfo.attestation?.teachrAddress
        ?? teachr?.address?.formattedAddress
        ?? "Votre adresse enregistrée";
    const distance = travelInfo.distanceKm != null ? travelInfo.distanceKm.toFixed(1) : "-";

    const handleSign = async () => {
        if (!mode) {
            setError("Veuillez sélectionner votre mode de transport.");
            return;
        }
        if (!accepted) {
            setError("Veuillez certifier l'exactitude des informations pour signer l'attestation.");
            return;
        }
        setError("");
        setLoad(true);
        try {
            const attestation = await TravelRepository.signAttestation(teachr?.id!, clientId, mode);
            // Justificatifs optionnels : l'échec d'un upload ne bloque pas la déclaration
            for (const doc of documents) {
                try {
                    await TravelRepository.addAttestationDocument(attestation.id, doc);
                } catch (e) {
                    console.warn("Upload justificatif échoué", e);
                }
            }
            onSigned();
        } catch (e) {
            dispatch({
                type: SHOW_ERROR,
                payload: "La signature de l'attestation a échoué. Veuillez réessayer.",
                title: "Attention !",
            });
        } finally {
            setLoad(false);
        }
    };

    return (
        <DialogLayout
            title="Attestation sur l'honneur de déplacement ✍️"
            open={open}
            handleClose={onClose}
            handleCloseIcon={onClose}
            customDialogActions="hidden"
            maxWidth="md"
            text={
                <div className={`${common.body} space-y-4`}>
                    {travelInfo.addressChangedSinceAttestation && <AddressChangeAlert reason={travelInfo.invalidationReason} />}
                    <p>
                        La distance entre votre adresse et celle de votre {getActiveClientBrandConfig()} est
                        de <span className={`font-semibold ${classes.primaryText}`}>{distance} km</span>{" "}
                        (entre {travelInfo.thresholdLow} et {travelInfo.thresholdHigh} km). Une attestation
                        sur l'honneur est nécessaire : elle n'est à signer{" "}
                        <span className="font-semibold">qu'une seule fois</span> pour
                        {getActiveClientBrandConfig('','cet ', 'ce ')}, tant que son adresse ne change pas. Une fois signée,
                        votre déclaration est <span className="font-semibold">validée automatiquement</span> et
                        suit son cours normalement.
                    </p>

                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 space-y-2">
                        <p className="font-semibold">Document pré-rempli :</p>
                        <p>
                            Je soussigné(e) <span className="font-semibold">{teachr?.firstName} {teachr?.lastName}</span>,
                            demeurant au <span className="font-semibold">{teachrAddress}</span>, atteste sur
                            l'honneur me déplacer au domicile de <span className="font-semibold">{clientName}</span> pour
                            y dispenser les {getActivePrestationBrandConfig("s")} que je déclare (distance estimée :{" "}
                            <span className="font-semibold">{distance} km</span>).
                        </p>
                    </div>

                    <div>
                        <p className={`font-semibold mb-2 ${classes.primaryText}`}>Mode de transport utilisé :</p>
                        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                            {Object.entries(travelInfo.travelModes ?? {}).map(([value, label]) => (
                                <FormControlLabel key={value} value={value} control={<Radio size="small" />} label={<p className={classes.fontFamily}>{label}</p>} />
                            ))}
                        </RadioGroup>
                    </div>

                    <div>
                        <p className="font-semibold mb-2">Justificatifs (optionnels) :</p>
                        <FileUploader
                            multiple
                            handleChange={(files: FileList) => setDocuments((prev) => [...prev, ...Array.from(files)])}
                            name="justificatifs"
                            types={FILE_TYPES}
                            label="Déposez ou cliquez pour ajouter vos justificatifs"
                        />
                        {documents.length > 0 && (
                            <ul className="mt-2 text-sm list-disc pl-5">
                                {documents.map((doc, index) => (
                                    <li key={index}>
                                        {doc.name}{" "}
                                        <button
                                            type="button"
                                            className="underline text-red-500"
                                            onClick={() => setDocuments((prev) => prev.filter((_, i) => i !== index))}
                                        >
                                            retirer
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <FormControlLabel
                        control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />}
                        label={<p className={classes.fontFamily}>Je certifie l'exactitude des informations ci-dessus et je signe électroniquement cette attestation.</p>}
                        
                    />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                </div>
            }
            btnChildren={
                <DialogActions sx={{ py: 2.5, px: 3, borderTop: "1px solid", borderColor: "divider" }} className={common.body}>
                    <ButtonField variant="outlined" onClick={onClose} label="Annuler" disabled={load} />
                    <ButtonField
                        variant="contained"
                        onClick={handleSign}
                        label="Signer et confirmer"
                        loading={load}
                        disabled={load}
                    />
                </DialogActions>
            }
        />
    );
};

export default AttestationModal;
